import {
  STATIC_COMPONENT_TYPES,
} from 'src/components/DynamicCriteriaGenerator/constants';

/**
 * Auto-valid component types that don't require data selection
 * Re-exported from constants for backwards compatibility
 */
export const AUTO_VALID_COMPONENT_TYPES = STATIC_COMPONENT_TYPES;

const COMPONENT_PLACEHOLDER_REGEX = /\{\{[^}]+\}\}/;

/**
 * Regex to match and extract component IDs from placeholders like {{ comp_123 }}
 */
const COMPONENT_ID_REGEX = /\{\{\s*([^}\s]+)\s*\}\}/g;

/**
 * Regex to match template placeholders like [CODE_A], [PAYER_TYPE], etc.
 * These are guide markers that users must replace with actual component pills.
 */
const TEMPLATE_PLACEHOLDER_REGEX = /\[[A-Z_]+\]/;

/**
 * Regex to detect clickable placeholder spans from templates.
 * These indicate unfilled template placeholders that need user action.
 */
const CLICKABLE_PLACEHOLDER_REGEX = /class="template-placeholder"/;

/**
 * Extract all component IDs from a criteria template string.
 * Returns an array of component IDs found in {{ componentId }} placeholders.
 */
export const extractComponentIds = (template: string): string[] => {
  const ids: string[] = [];
  let match;
  // Reset regex lastIndex to ensure we start from the beginning
  COMPONENT_ID_REGEX.lastIndex = 0;
  while ((match = COMPONENT_ID_REGEX.exec(template)) !== null) {
    ids.push(match[1]);
  }
  return ids;
};

export interface DynamicCriteria {
  criteria_template?: string;
  criteria_inputs?: Record<string, DynamicComponentInput>;
  listId?: string | number;
}

export interface DynamicComponentInput {
  type?: string;
  connector?: string;
  value?: any; // Data payload — array for regular types, single value for singletons
  ui_metadata?: any[];
  ui_key?: string;
}

export const hasComponentPlaceholders = (template: string): boolean => {
  return COMPONENT_PLACEHOLDER_REGEX.test(template);
};

/**
 * Check if template contains unreplaced template placeholders like [CODE_A], [PAYER_TYPE], etc.
 * These are guide markers from templates that users must replace with actual component pills.
 */
export const hasTemplatePlaceholders = (template: string): boolean => {
  return TEMPLATE_PLACEHOLDER_REGEX.test(template);
};

/**
 * Check if template contains clickable placeholder spans from templates.
 * These indicate unfilled {{ input }} placeholders that the user needs to click to fill.
 */
export const hasClickablePlaceholders = (template: string): boolean => {
  return CLICKABLE_PLACEHOLDER_REGEX.test(template);
};

/**
 * Check if template has any unfilled placeholders (either format).
 * Returns true if the template has:
 * - [CODE_A] style guide markers, OR
 * - Clickable template-placeholder spans from loaded templates
 */
export const hasUnfilledPlaceholders = (template: string): boolean => {
  return hasTemplatePlaceholders(template) || hasClickablePlaceholders(template);
};

/**
 * Check if criteria has any {{ input }} placeholders that are not yet filled with valid data.
 * This is used for progress validation - returns true if ANY placeholder lacks valid data.
 *
 * A placeholder is considered unfilled if:
 * - It's in [CODE_A] format (user guide marker)
 * - It's a clickable template-placeholder span
 * - It's a {{ input_name }} pattern without a valid criteria_inputs entry
 */
export const hasUnfilledCriteriaPlaceholders = (
  criteria: DynamicCriteria | null | undefined
): boolean => {
  if (!criteria?.criteria_template) {
    return false; // No template means nothing to check
  }

  const template = criteria.criteria_template;

  // Check for [CODE_A] style or HTML placeholder spans
  if (hasUnfilledPlaceholders(template)) {
    return true;
  }

  // Extract all {{ input }} patterns and check if they have valid data
  const componentIds = extractComponentIds(template);
  if (componentIds.length === 0) {
    return false; // No placeholders
  }

  const criteriaInputs = criteria.criteria_inputs || {};

  // If ANY placeholder lacks valid data, return true
  return componentIds.some((componentId) => {
    const input = criteriaInputs[componentId];
    if (!input) {
      return true; // No input entry = unfilled
    }
    return !isComponentValid(input); // Invalid data = unfilled
  });
};

export const isComponentValid = (input: DynamicComponentInput): boolean => {
  if (input.type && AUTO_VALID_COMPONENT_TYPES.includes(input.type as any)) {
    return true;
  }

  const value = input.value;
  return Array.isArray(value) ? value.length > 0 : !!value;
};

export const validateDynamicCriteria = (
  criteria: DynamicCriteria | null | undefined
): boolean => {
  if (!criteria?.criteria_template) {
    return false;
  }

  // Check for any unfilled placeholders (either [CODE_A] style or clickable template spans)
  if (hasUnfilledPlaceholders(criteria.criteria_template)) {
    return false;
  }

  // Extract all component IDs from the template
  const componentIds = extractComponentIds(criteria.criteria_template);

  // If no components in template, it's valid
  if (componentIds.length === 0) {
    return true;
  }

  const criteriaInputs = criteria.criteria_inputs || {};

  // Check that EVERY component in the template has a valid corresponding input
  const allComponentsValid = componentIds.every((componentId) => {
    const input = criteriaInputs[componentId];
    // Component must exist in criteria_inputs and have valid data
    if (!input) {
      return false;
    }
    return isComponentValid(input);
  });

  return allComponentsValid;
};

export const validatePatientJourneyCriteria = (
  indexEventCriteria: DynamicCriteria | null | undefined,
  targetEventCriteria: DynamicCriteria | null | undefined
): boolean => {
  if (
    !indexEventCriteria?.criteria_template ||
    !targetEventCriteria?.criteria_template
  ) {
    return false;
  }

  // Check for any unfilled placeholders (either [CODE_A] style or clickable template spans)
  if (
    hasUnfilledPlaceholders(indexEventCriteria.criteria_template) ||
    hasUnfilledPlaceholders(targetEventCriteria.criteria_template)
  ) {
    return false;
  }

  // Extract all component IDs from both templates
  const indexComponentIds = extractComponentIds(
    indexEventCriteria.criteria_template
  );
  const targetComponentIds = extractComponentIds(
    targetEventCriteria.criteria_template
  );

  const indexCriteriaInputs = indexEventCriteria.criteria_inputs || {};
  const targetCriteriaInputs = targetEventCriteria.criteria_inputs || {};

  // Check that EVERY component in index template has a valid corresponding input
  const indexAllComponentsValid = indexComponentIds.every((componentId) => {
    const input = indexCriteriaInputs[componentId];
    if (!input) {
      return false;
    }
    return isComponentValid(input);
  });

  // Check that EVERY component in target template has a valid corresponding input
  const targetAllComponentsValid = targetComponentIds.every((componentId) => {
    const input = targetCriteriaInputs[componentId];
    if (!input) {
      return false;
    }
    return isComponentValid(input);
  });

  // Both templates must have all their components validated
  // If a template has no components, it's considered valid
  return (
    (indexComponentIds.length === 0 || indexAllComponentsValid) &&
    (targetComponentIds.length === 0 || targetAllComponentsValid)
  );
};
